// frontend/app/components/lessonPlans/present/LessonPresentMode.js
//
// Present mode: one teacher, one screen, a projector, and a class with no
// devices at all.
//
// The workspace this replaces assumes the opposite — that whoever is reading it
// can type an answer, press Check and have it recorded. Every one of those
// assumptions is false in a room where the students answer out loud, so this is
// a different surface over the same authored blocks rather than a restyled
// workspace.
//
// THIS COMPONENT RECORDS NOTHING, AND CANNOT.
// There are no individual students here to attribute an answer to, so there is
// no answer to store. It takes no onAnswer, no onCheck and no session state; it
// never calls an evaluate endpoint and never touches mastery. The only callback
// that reaches the outside world for content is `onOpenAsset`, which launches a
// scene the caller already owns — a launch, not a write. Keeping the props that
// could record absent, rather than present and unused, is what makes that
// property checkable instead of merely intended.
//
// The Saras panel does not weaken that. Asking Saras a question is not a
// student answer — nobody in the room has a device to answer with — and the
// panel's only reachable endpoint is /api/ask_ai, which replies with text and
// stores nothing about anyone. See presentAskClient.js.
//
// Neither does the presenter window. It is a second view of this same state on
// a second screen: it shows the notes, and the only thing it sends back is the
// same back / reveal / next the on-screen controls already do. There is no
// other callback it can reach. See usePresenterWindow.js.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";

import { getPresentColors, getPresentType } from "./presentTheme";
import {
  hasNotes as stepHasNotes,
  presentBlockKey,
  presentBlocks,
  revealableBlockIds,
  stepNotes,
} from "./presentRules";
import PresentBlock from "./PresentBlock";
import PresentNotes from "./PresentNotes";
import PresentSarasPanel from "./PresentSarasPanel";
import {
  PresentBottomBar,
  PresentKeyHint,
  PresentNotice,
  PresentTopBar,
} from "./PresentChrome";
import usePresentKeys from "./usePresentKeys";
import usePresenterWindow from "./usePresenterWindow";
import usePresentTransition from "./usePresentTransition";
import { getPhaseMeta } from "../lessonPlanTheme";

const LessonPresentMode = ({
  // The same runtime payload LessonPlanWorkspace takes.
  lesson,
  stepIndex = 0,
  onStepChange,
  onExit,
  // The 3D scene and the video play on the slide itself — the workspace that
  // normally owns those players is not mounted in this mode. They need a token
  // and a language, nothing more.
  authToken,
  localLanguage = "en",
  languageId = 1,
  // Still used by the topic quiz, which is handed back rather than opened here:
  // its player records answers and present mode must never write one.
  onOpenAsset,
  // The signed-in teacher, so an ask threads onto their own session rather than
  // an anonymous one. Optional: the panel works without it.
  userId,
  // An override for the ask itself, for a caller that would rather supply the
  // app's own. Omitted, the panel uses present mode's client — the feature
  // works with nothing wired up, which is the only way it can be trusted to
  // work at all.
  askSaras,
  // Fired whenever the Saras panel opens or closes. Optional, and only needed
  // by a caller that has to know — see the fullscreen note on Escape below.
  onSarasOpenChange,
  // Fired whenever the presenter window opens or closes, and needed for the
  // same reason: opening a popup can knock the main window out of browser
  // fullscreen, and a caller that reads "fullscreen ended" as "the teacher is
  // done" would tear the lesson down in front of the class for it. Optional —
  // the presenter window works without it; the lesson is just at the mercy of
  // whatever the caller does with fullscreenchange.
  onPresenterOpenChange,
  isDarkMode = false,
  t,
}) => {
  const translate = t || ((value) => value);
  const colors = getPresentColors(isDarkMode);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  // Present mode is usually full screen, but it can be handed a content area
  // that is narrower than the window; the type scale must size off the surface
  // it actually has. Window width covers the first frame, before layout.
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const width = measuredWidth || windowWidth;
  const type = useMemo(
    () => getPresentType(width, windowHeight),
    [width, windowHeight]
  );

  const steps = useMemo(
    () => (Array.isArray(lesson?.steps) ? lesson.steps : []),
    [lesson]
  );
  const total = steps.length;

  // The caller owns the index, so it can hand over one that is out of range —
  // after switching lessons, say. Clamping here means a bad index shows the
  // nearest real step instead of a blank wall.
  const index = Math.max(0, Math.min(Number(stepIndex) || 0, Math.max(0, total - 1)));
  const step = total > 0 ? steps[index] : null;

  const blocks = useMemo(() => presentBlocks(step), [step]);
  const revealIds = useMemo(() => revealableBlockIds(step), [step]);
  const notes = useMemo(() => stepNotes(step), [step]);
  const notesAvailable = stepHasNotes(step);

  /**
   * How many of this step's revealable blocks have been shown, in authored
   * order — the PowerPoint build-step model. One press shows the next one;
   * when they are all shown, the same press moves to the next step.
   */
  const [revealCount, setRevealCount] = useState(0);

  // Notes are off at the start of every session and stay wherever the teacher
  // put them from step to step: a teacher who turned them on for one step is
  // mid-lesson and does not want to re-press N on every slide.
  const [notesVisible, setNotesVisible] = useState(false);

  // Saras is off by default and, unlike notes, does NOT persist across steps.
  // The panel covers the stage, so carrying it into the next slide would hide
  // the slide the teacher just moved to; and an answer to the last step's
  // question is stale the moment the lesson moves on.
  const [sarasOpen, setSarasOpen] = useState(false);

  // The step's own ai_prompt questions are what the panel opens on — the
  // teacher planned to ask these, so they are the starting point rather than a
  // blank box. A step with none still gets the panel: a class asks its own
  // questions on slides that never planned for one.
  // A question pressed on the slide opens the rail already asking it. Held
  // here rather than in the panel because the press happens on the slide and
  // the rail may not exist yet.
  const [sarasQuestion, setSarasQuestion] = useState(null);
  // Set only for an authored summary, where the rail is showing words the
  // lesson author wrote rather than anything a model produced. Paired with
  // `sarasQuestion`, which is then the heading over it, not something to send.
  const [sarasAnswer, setSarasAnswer] = useState(null);
  const askQuestionFromSlide = useCallback((question) => {
    const text = String(question || "").trim();
    if (!text) return;
    setSarasQuestion(text);
    // A real question replaces an authored summary; leaving the old text set
    // would show the model's question over the author's answer.
    setSarasAnswer(null);
    setSarasOpen(true);
  }, []);

  /**
   * Open the rail on an asset's authored summary.
   *
   * Deliberately not routed through `askQuestionFromSlide`: that path asks, and
   * this one must not. Nothing here reaches the network, so present mode's
   * "records nothing" guarantee holds by construction rather than by care.
   */
  const showSummary = useCallback((summary) => {
    const text = String(summary?.text || "").trim();
    if (!text) return;
    setSarasQuestion(String(summary?.label || "").trim() || null);
    setSarasAnswer(text);
    setSarasOpen(true);
  }, []);

  const sarasQuestions = useMemo(
    () =>
      blocks
        .filter((block) => block?.type === "ai_prompt")
        .flatMap((block) => (Array.isArray(block.questions) ? block.questions : []))
        .filter((question) => typeof question === "string" && question.trim()),
    [blocks]
  );

  // A reveal belongs to the step that authored it. Arriving at a new step with
  // the previous step's answers already showing would give the next question
  // away before it was asked.
  const stageScrollRef = useRef(null);

  useEffect(() => {
    setRevealCount(0);
    setSarasOpen(false);
    setSarasQuestion(null);
    setSarasAnswer(null);
    // Start every slide at its own top. The stage is one scroller that swaps
    // its children, so without this a long step leaves the next one opening
    // half way down — past its heading, in front of the class.
    stageScrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [index, lesson?.lesson_plan_id]);

  const opacity = usePresentTransition(index);

  const goTo = useCallback(
    (next) => {
      if (!onStepChange || total === 0) return;
      const clamped = Math.max(0, Math.min(next, total - 1));
      if (clamped !== index) onStepChange(clamped);
    },
    [index, onStepChange, total]
  );

  const goPrevious = useCallback(() => goTo(index - 1), [goTo, index]);
  const goNext = useCallback(() => goTo(index + 1), [goTo, index]);

  const revealsLeft = Math.max(0, revealIds.length - revealCount);

  // Every exit goes through here. The question pressed on the slide lives in
  // this component, which outlives the panel, so leaving it set meant a reopen
  // looked like a fresh press and asked Saras the same thing again.
  const closeSaras = useCallback(() => {
    setSarasOpen(false);
    setSarasQuestion(null);
    setSarasAnswer(null);
  }, []);

  const toggleSaras = useCallback(() => {
    setSarasOpen((open) => {
      if (open) {
        setSarasQuestion(null);
        setSarasAnswer(null);
      }
      return !open;
    });
  }, []);

  /** Space / Enter / the clicker's big button: reveal, else move on. */
  const advance = useCallback(() => {
    // With Saras up, the next beat is putting Saras away. Revealing an answer
    // behind the panel would spend a build step the class never saw, and the
    // teacher would then reveal it a second time to a room that has already
    // been shown it.
    if (sarasOpen) {
      closeSaras();
      return;
    }
    if (revealsLeft > 0) {
      setRevealCount((count) => count + 1);
      return;
    }
    goNext();
  }, [goNext, revealsLeft, sarasOpen]);

  /**
   * Reveal the next answer and stop there.
   *
   * The presenter window's Reveal button, and only that. `advance` is the
   * clicker's one-button behaviour — reveal, then move on when there is nothing
   * left — which is right for a thumb on a remote and wrong for a labelled
   * button a teacher reads before pressing. A button that says Reveal must
   * never be the thing that changed the slide.
   */
  const revealOnly = useCallback(() => {
    // Same reasoning as `advance`: an answer revealed behind the Saras panel is
    // an answer the room never saw being revealed.
    if (sarasOpen) {
      closeSaras();
      return;
    }
    setRevealCount((count) => Math.min(count + 1, revealIds.length));
  }, [closeSaras, revealIds.length, sarasOpen]);

  const toggleNotes = useCallback(() => setNotesVisible((value) => !value), []);

  const handleExit = useCallback(() => {
    if (onExit) onExit();
  }, [onExit]);

  /**
   * The second screen. The lesson stays here; the notes go over there.
   *
   * Everything it needs is state this component already owns, so the two
   * windows cannot disagree: there is one step index, one reveal count, and the
   * popup is a view of them rather than a copy.
   */
  const presenter = usePresenterWindow({
    lesson,
    steps,
    index,
    revealCount,
    isDarkMode,
    onPrevious: goPrevious,
    onNext: goNext,
    onReveal: revealOnly,
    onAdvance: advance,
    onOpenChange: onPresenterOpenChange,
    // A lesson with no steps never renders the chrome that offers this, and
    // must not be openable by keyboard either.
    enabled: total > 0,
    t,
  });

  usePresentKeys({
    // Step navigation keeps working with the panel open, and closes it on the
    // way past (see the step effect above). A clicker sends nothing but these,
    // so a teacher holding one and looking at the wall must never be able to
    // get stuck behind a panel they cannot see a way out of.
    onPrevious: goPrevious,
    onNext: goNext,
    onAdvance: advance,
    onExit: handleExit,
    onToggleNotes: notesAvailable ? toggleNotes : undefined,
    // A lesson with no steps returns before the panel is ever rendered, so the
    // key must not be able to open one that nothing will draw.
    onToggleSaras: total > 0 ? toggleSaras : undefined,
    // P goes straight to window.open from inside the keydown, which is the only
    // moment a pop-up blocker will allow it.
    onTogglePresenter: presenter.supported && total > 0 ? presenter.toggle : undefined,
    // Present only while the panel is open, which is how Escape's precedence is
    // expressed: the hook closes the panel and leaves the lesson running.
    onCloseSaras: sarasOpen ? closeSaras : undefined,
  });

  // Let a caller that needs it know the panel is up. The cleanup reports false
  // on unmount as well as on change, so a caller holding this in a ref is never
  // left guarding a panel that no longer exists.
  useEffect(() => {
    if (!onSarasOpenChange) return undefined;
    onSarasOpenChange(sarasOpen);
    return () => onSarasOpenChange(false);
  }, [onSarasOpenChange, sarasOpen]);

  /** Clicking one block's own Reveal shows everything up to and including it. */
  const revealUpTo = useCallback(
    (position) => setRevealCount((count) => Math.max(count, position + 1)),
    []
  );

  const phase = getPhaseMeta(step?.phase);

  if (total === 0) {
    return (
      <View style={[styles.container, { backgroundColor: colors.pageBg }]}>
        <PresentTopBar
          lesson={lesson}
          step={null}
          index={0}
          total={0}
          colors={colors}
          type={type}
          notesVisible={false}
          hasNotes={false}
          onExit={handleExit}
          t={t}
        />
        <View style={styles.emptyWrap}>
          <Text style={[styles.emptyText, type.stepTitle, { color: colors.textMuted }]}>
            {translate("This lesson has no steps to present.")}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View
      style={[styles.container, { backgroundColor: colors.pageBg }]}
      onLayout={(event) => setMeasuredWidth(event.nativeEvent.layout.width)}
    >
      <PresentTopBar
        lesson={lesson}
        step={step}
        index={index}
        total={total}
        colors={colors}
        type={type}
        notesVisible={notesVisible}
        hasNotes={notesAvailable}
        onToggleNotes={toggleNotes}
        sarasOpen={sarasOpen}
        onToggleSaras={toggleSaras}
        presenterOpen={presenter.isOpen}
        onTogglePresenter={presenter.supported ? presenter.toggle : undefined}
        onExit={handleExit}
        t={t}
      />

      {/* The blocker is silent — window.open returns null and throws nothing —
          so this strip is the only thing standing between the teacher and a
          button that appears broken. It names the fix rather than the fault,
          and it goes away when they put it away. */}
      {presenter.isBlocked ? (
        <PresentNotice
          colors={colors}
          type={type}
          message={translate(
            "Your browser blocked the presenter window. Allow pop-ups for this site, then press P or the Presenter view button again."
          )}
          actionLabel={translate("Dismiss")}
          onDismiss={presenter.dismissBlocked}
        />
      ) : null}

      <View style={styles.stageArea}>
        <Animated.View style={[styles.stage, { opacity }]}>
          <ScrollView
            ref={stageScrollRef}
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.titleBlock}>
              <Text style={[styles.stepPhase, type.eyebrow, { color: phase.accent }]}>
                {translate(phase.label)}
                {step?.estimated_minutes
                  ? `  ·  ${step.estimated_minutes} ${translate("min")}`
                  : ""}
              </Text>
              <Text
                style={[styles.stepTitle, type.stepTitle, { color: colors.textPrimary }]}
              >
                {step?.title}
              </Text>
            </View>

            {blocks.length === 0 ? (
              // A step can be entirely spoken — all of its content may have been a
              // teacher_tip, or a mastery map that means nothing without a
              // student. Say so plainly rather than showing an empty wall.
              <Text style={[styles.emptyText, type.caption, { color: colors.textMuted }]}>
                {notesAvailable
                  ? translate("Nothing to show on screen for this step — press N for your notes.")
                  : translate("Nothing to show on screen for this step.")}
              </Text>
            ) : (
              blocks.map((block, position) => {
                const key = presentBlockKey(step, block, position);
                const revealPosition = revealIds.indexOf(key);
                const revealed = revealPosition >= 0 && revealPosition < revealCount;

                return (
                  <View key={key} style={styles.blockWrap}>
                    <PresentBlock
                      block={block}
                      colors={colors}
                      type={type}
                      isDarkMode={isDarkMode}
                      revealed={revealed}
                      onReveal={
                        revealPosition >= 0
                          ? () => revealUpTo(revealPosition)
                          : undefined
                      }
                      onOpenAsset={onOpenAsset}
                      onAskQuestion={askQuestionFromSlide}
                      onShowSummary={showSummary}
                      authToken={authToken}
                      localLanguage={localLanguage}
                      languageId={languageId}
                      t={t}
                    />
                  </View>
                );
              })
            )}
          </ScrollView>
        </Animated.View>

        {/* Over the stage, and only the stage: the top bar stays reachable so
            the teacher can close this the way they opened it, and the step
            counter never disappears mid-lesson. Absolutely positioned rather
            than swapped in, so the slide underneath keeps its scroll position
            and does not move while a long answer scrolls inside the panel. */}
        {sarasOpen ? (
          <View style={styles.sarasOverlay}>
            <PresentSarasPanel
              initialQuestion={sarasQuestion}
              initialAnswer={sarasAnswer}
              questions={sarasQuestions}
              colors={colors}
              type={type}
              onClose={closeSaras}
              askSaras={askSaras}
              userId={userId}
              language={localLanguage}
              t={t}
            />
          </View>
        ) : null}
      </View>

      {notesVisible ? (
        <View style={styles.notesWrap}>
          <PresentNotes notes={notes} colors={colors} type={type} t={t} />
        </View>
      ) : null}

      <PresentBottomBar
        index={index}
        total={total}
        colors={colors}
        type={type}
        revealsLeft={revealsLeft}
        onPrevious={goPrevious}
        onNext={goNext}
        onAdvance={advance}
        t={t}
      />
      <PresentKeyHint
        colors={colors}
        type={type}
        sarasOpen={sarasOpen}
        presenterAvailable={presenter.supported}
        t={t}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, width: "100%", minHeight: 0 },

  // The region an overlay is allowed to cover — everything between the two
  // chrome bars. Wrapping the stage rather than positioning against the whole
  // container is what keeps the panel off the step counter and the exit button
  // without either of them having to know a height.
  stageArea: { flex: 1, minHeight: 0 },
  // A right-hand rail, not a curtain. Saras sits on the right everywhere else
  // in the app, and covering the whole wall to read an answer hides the step
  // the answer is about — the class is still reading it. The left edge is open
  // so the lesson shows through beside the rail.
  sarasOverlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "34%",
    minWidth: 380,
    maxWidth: 620,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },

  stage: { flex: 1, minHeight: 0 },
  scroll: { flex: 1 },
  // flexGrow + centre means a short slide sits in the middle of the wall, the
  // way a slide should, while a long one still scrolls rather than clipping.
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    paddingHorizontal: 48,
    paddingVertical: 40,
  },

  titleBlock: { width: "100%", maxWidth: 1180, alignSelf: "center", marginBottom: 36 },
  stepPhase: {
    fontWeight: "800",
    letterSpacing: 1.3,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  stepTitle: { fontWeight: "800" },

  blockWrap: { width: "100%", marginBottom: 40 },

  notesWrap: { paddingHorizontal: 24, paddingBottom: 8 },

  emptyWrap: { flex: 1, alignItems: "center", justifyContent: "center", padding: 40 },
  emptyText: { textAlign: "center", fontWeight: "600" },
});

export default LessonPresentMode;
